import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Check, Loader2, ArrowLeft, Sparkles, Zap, Shield, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios, { API } from '@/utils/api';
import { toast } from 'sonner';

const UpgradePage = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [selectedInterval, setSelectedInterval] = useState('year'); // month or year
  const [user, setUser] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  // SECURITY: Auth via httpOnly cookies
  const fetchData = async () => {
    try {
      // Fetch packages (public)
      const packagesRes = await axios.get(`${API}/payments/packages`);
      setPackages(packagesRes.data.packages || []);
      
      // Fetch user info (auth via cookie)
      try {
        const userRes = await axios.get(`${API}/auth/me`);
        setUser(userRes.data);
      } catch (err) {
        // Not logged in - this is expected for non-authenticated users
        console.debug('User not logged in:', err.message);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (packageId) => {
    // Check if user is logged in by trying to get user data
    if (!user) {
      toast.error('Please log in to upgrade');
      navigate('/login');
      return;
    }

    setProcessing(packageId);
    
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin
      });

      if (response.data.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setProcessing(null);
    }
  };

  const monthlyPackage = packages.find(p => p.id === 'premium_monthly');
  const yearlyPackage = packages.find(p => p.id === 'premium_yearly');
  
  const yearlySavings = monthlyPackage && yearlyPackage 
    ? ((monthlyPackage.amount * 12) - yearlyPackage.amount).toFixed(2)
    : '44.88';

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1a0527] via-[#2D063E] to-[#1a0527] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#df1283] animate-spin" />
      </div>
    );
  }

  // If user is already premium or founding member
  if (user && (user.membership_tier === 'premium' || user.membership_tier === 'founding_member')) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1a0527] via-[#2D063E] to-[#1a0527] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-[#2D063E]/50 border border-[#df1283]/30 rounded-2xl p-8">
            <Crown className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">
              You're Already {user.membership_tier === 'founding_member' ? 'a Founding Member' : 'Premium'}!
            </h1>
            <p className="text-gray-400 mb-6">
              {user.membership_tier === 'founding_member' 
                ? 'As a Founding Member, you have lifetime access to all premium features.'
                : 'You already have access to all premium features.'}
            </p>
            <Button 
              onClick={() => navigate('/dashboard')}
              className="bg-gradient-to-r from-[#df1283] to-[#FF7901]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0527] via-[#2D063E] to-[#1a0527] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back button */}
        <button 
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#df1283]/20 border border-[#df1283]/30 text-[#df1283] text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            Upgrade to Premium
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">
            Unlock Your Full Journaling Potential
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Get unlimited access to AI coaching, voice transcription, and premium features
          </p>
        </div>

        {/* Interval Toggle */}
        <div className="flex justify-center mb-8">
          <div className="bg-[#1a0527] p-1 rounded-full inline-flex">
            <button
              onClick={() => setSelectedInterval('month')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                selectedInterval === 'month'
                  ? 'bg-[#df1283] text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setSelectedInterval('year')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                selectedInterval === 'year'
                  ? 'bg-[#df1283] text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Yearly
              <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">
                Save ${yearlySavings}
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Card */}
        <div className="max-w-md mx-auto">
          <div className="bg-gradient-to-b from-[#2D063E] to-[#1a0527] border border-[#df1283]/50 rounded-2xl p-8 relative overflow-hidden">
            {/* Popular badge */}
            {selectedInterval === 'year' && (
              <div className="absolute top-4 right-4">
                <span className="px-3 py-1 bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white text-xs font-bold rounded-full">
                  BEST VALUE
                </span>
              </div>
            )}

            <div className="text-center mb-6">
              <Crown className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Premium</h2>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-5xl font-bold text-white">
                  ${selectedInterval === 'year' ? yearlyPackage?.amount : monthlyPackage?.amount}
                </span>
                <span className="text-gray-400">
                  /{selectedInterval === 'year' ? 'year' : 'month'}
                </span>
              </div>
              {selectedInterval === 'year' && (
                <p className="text-green-400 text-sm mt-2">
                  That's just ${(yearlyPackage?.amount / 12).toFixed(2)}/month
                </p>
              )}
            </div>

            {/* Features */}
            <ul className="space-y-4 mb-8">
              {[
                { icon: Zap, text: 'Unlimited journal entries' },
                { icon: Sparkles, text: 'Unlimited AI coaching sessions' },
                { icon: Star, text: 'Unlimited voice transcription' },
                { icon: Crown, text: 'Premium badges & achievements' },
                { icon: Shield, text: 'Priority customer support' },
                { icon: Check, text: 'Export journals as PDF' },
                { icon: Check, text: 'Advanced mood analytics' },
                { icon: Check, text: 'Custom themes' },
              ].map((feature) => (
                <li key={feature.text} className="flex items-center gap-3 text-gray-300">
                  <div className="w-6 h-6 rounded-full bg-[#df1283]/20 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-3.5 h-3.5 text-[#df1283]" />
                  </div>
                  {feature.text}
                </li>
              ))}
            </ul>

            {/* CTA Button */}
            <Button
              onClick={() => handleUpgrade(selectedInterval === 'year' ? 'premium_yearly' : 'premium_monthly')}
              disabled={processing !== null}
              className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-[#df1283] to-[#FF7901] hover:opacity-90 transition-opacity"
            >
              {processing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Upgrade Now
                  <Sparkles className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>

            <p className="text-center text-gray-500 text-xs mt-4">
              Secure payment powered by Stripe. Cancel anytime.
            </p>
          </div>
        </div>

        {/* Guarantee */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-2 text-gray-400 text-sm">
            <Shield className="w-4 h-4 text-green-400" />
            30-day money-back guarantee
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradePage;
